import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Package, Truck, Store, User, MapPin, CreditCard, Clock, Building2, ExternalLink, PiggyBank, Info } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["Payée", "En préparation", "Expédiée", "Livrée", "Retirée", "Annulée"];
const eur = (n) => `${(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const STATUS_COLOR = {
  "Payée": "bg-green-100 text-green-700", "En préparation": "bg-blue-100 text-blue-700",
  "Expédiée": "bg-indigo-100 text-indigo-700", "Livrée": "bg-emerald-100 text-emerald-700",
  "Retirée": "bg-emerald-100 text-emerald-700", "Annulée": "bg-red-100 text-red-700",
  "À facturer": "bg-amber-100 text-amber-700", "pending": "bg-stone/20 text-stone",
};

const Section = ({ icon: Icon, title, children, testid }) => (
  <div className="rounded-xl bg-white border border-border p-5" data-testid={testid}>
    <h3 className="font-semibold text-forest mb-3 flex items-center gap-2 text-sm uppercase tracking-wide"><Icon size={16} className="text-brand" /> {title}</h3>
    {children}
  </div>
);

const Row = ({ label, value }) => value ? (
  <div className="flex justify-between gap-4 text-sm py-1"><span className="text-stone">{label}</span><span className="text-forest text-right font-medium">{value}</span></div>
) : null;

function LoadingPanel({ logistics, vehicles }) {
  const L = logistics || { total_weight_kg: 0, total_volume_m3: 0, total_footprint_m2: 0 };
  const rate = (v) => {
    const rw = v.payload_kg ? L.total_weight_kg / v.payload_kg : 0;
    const rv = v.volume_m3 ? L.total_volume_m3 / v.volume_m3 : 0;
    const rf = v.surface_m2 ? L.total_footprint_m2 / v.surface_m2 : 0;
    const max = Math.max(rw, rv, rf);
    const driver = max === 0 ? "" : rw === max ? "poids" : rv === max ? "volume" : "surface";
    return { pct: Math.round(max * 100), driver };
  };
  const ranked = vehicles.map((v) => ({ v, ...rate(v) }))
    .sort((a, b) => (a.pct <= 100 && b.pct <= 100) ? b.pct - a.pct : (a.pct <= 100 ? -1 : b.pct <= 100 ? 1 : a.pct - b.pct));
  const barColor = (p) => p > 100 ? "bg-red-500" : p > 85 ? "bg-amber-500" : "bg-green-500";
  return (
    <Section icon={Truck} title="Chargement & camion" testid="order-loading">
      <div className="grid grid-cols-3 gap-3 text-center mb-4">
        <div className="rounded-lg bg-[#F0EFEA] p-3"><p className="font-display text-xl text-forest" data-testid="load-weight">{L.total_weight_kg >= 1000 ? `${(L.total_weight_kg / 1000).toFixed(2)} t` : `${Math.round(L.total_weight_kg)} kg`}</p><p className="text-[11px] text-stone">Poids total</p></div>
        <div className="rounded-lg bg-[#F0EFEA] p-3"><p className="font-display text-xl text-forest" data-testid="load-volume">{L.total_volume_m3} m³</p><p className="text-[11px] text-stone">Volume</p></div>
        <div className="rounded-lg bg-[#F0EFEA] p-3"><p className="font-display text-xl text-forest">{L.total_footprint_m2} m²</p><p className="text-[11px] text-stone">Emprise sol</p></div>
      </div>
      {vehicles.length === 0 ? (
        <p className="text-xs text-stone">Aucun véhicule. Ajoutez votre flotte pour voir le taux de remplissage.</p>
      ) : (
        <div className="space-y-3" data-testid="load-vehicles">
          {ranked.map(({ v, pct, driver }, i) => (
            <div key={v.id} data-testid={`load-vehicle-${v.id}`}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="font-medium text-forest flex items-center gap-1.5">{i === 0 && pct <= 100 && <span className="text-[9px] bg-green-100 text-green-700 rounded-full px-1.5 py-0.5 font-bold">RECOMMANDÉ</span>}{v.name}</span>
                <span className={`font-bold ${pct > 100 ? "text-red-600" : "text-forest"}`}>{pct}%{pct > 100 ? " · surcharge" : ""}</span>
              </div>
              <div className="h-2.5 rounded-full bg-stone/15 overflow-hidden">
                <div className={`h-full ${barColor(pct)} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
              </div>
              <p className="text-[10px] text-stone mt-0.5">Limite atteinte : {driver || "—"} · {v.payload_kg} kg / {v.volume_m3} m³ / {v.surface_m2} m²</p>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

export default function OrderDetail() {
  const { orderId } = useParams();
  const [o, setO] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/orders/${orderId}`).then((r) => setO(r.data)).catch(() => toast.error("Commande introuvable"));
  useEffect(() => { load(); api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {}); }, [orderId]);

  const setStatus = async (status) => {
    setSaving(true);
    try { await api.put(`/orders/${orderId}/status`, { status }); toast.success("Statut mis à jour"); load(); }
    catch { toast.error("Erreur de mise à jour"); } finally { setSaving(false); }
  };

  if (!o) return <p className="text-stone">Chargement…</p>;

  const isTerms = o.payment_method === "30j_fin_de_mois" || o.payment_status === "terms";
  const acc = o.account;

  return (
    <div className="max-w-5xl">
      <Link to="/admin/commandes" className="inline-flex items-center gap-1.5 text-sm text-stone hover:text-forest mb-4" data-testid="back-to-orders"><ArrowLeft size={16} /> Retour aux commandes</Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl text-forest">Commande #{String(o.order_id).slice(0, 8).toUpperCase()}</h1>
          <p className="text-stone text-sm mt-1">{new Date(o.created_at).toLocaleString("fr-FR")} · {o.items?.length} article(s)</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${STATUS_COLOR[o.status] || "bg-stone/20 text-stone"}`} data-testid="order-current-status">{o.status}</span>
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${o.payment_status === "paid" ? "bg-green-100 text-green-700" : isTerms ? "bg-amber-100 text-amber-700" : "bg-stone/20 text-stone"}`}>
            {o.payment_status === "paid" ? "Payée (CB)" : isTerms ? "30j fin de mois" : "En attente"}
          </span>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.5fr_1fr] gap-5">
        {/* LEFT: items + totals */}
        <div className="space-y-5">
          <Section icon={Package} title="Articles commandés" testid="order-items">
            <div className="divide-y divide-border -mt-1">
              {o.items?.map((it, k) => (
                <div key={k} className="flex items-center gap-3 py-3">
                  {it.image && <img src={it.image} alt="" className="h-12 w-12 rounded-lg object-cover border border-border shrink-0" />}
                  <div className="flex-1 min-w-0">
                    {it.slug ? (
                      <Link to={`/boutique/${it.slug}`} target="_blank" className="text-sm font-medium text-forest hover:text-brand inline-flex items-center gap-1">{it.name} <ExternalLink size={12} /></Link>
                    ) : <p className="text-sm font-medium text-forest">{it.name}</p>}
                    <p className="text-xs text-stone">{eur(it.price)} × {it.quantity}{it.cost_price ? ` · coût ${eur(it.cost_price)}/u` : ""}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-semibold text-forest">{eur(it.line_total)}</p>
                    {it.line_margin !== undefined && <p className="text-[11px] text-green-600">+{eur(it.line_margin)}</p>}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-border space-y-1">
              <Row label="Sous-total" value={eur(o.subtotal)} />
              {o.discount > 0 && <Row label={`Remise ${o.coupon_code ? `(${o.coupon_code})` : ""}`} value={`- ${eur(o.discount)}`} />}
              <Row label={o.delivery?.mode === "delivery" ? `Livraison${o.delivery?.distance_km ? ` (~${o.delivery.distance_km} km)` : ""}` : "Retrait"} value={o.delivery_fee > 0 ? eur(o.delivery_fee) : "Offerte"} />
              <div className="flex justify-between gap-4 text-base pt-2 border-t border-border mt-2">
                <span className="font-semibold text-forest">Total</span>
                <span className="font-bold text-forest text-lg">{eur(o.total)}</span>
              </div>
            </div>
          </Section>

          <Section icon={PiggyBank} title="Rentabilité (interne)" testid="order-margin">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg bg-[#F0EFEA] p-3"><p className="font-display text-xl text-forest">{eur(o.subtotal - (o.discount || 0))}</p><p className="text-[11px] text-stone">CA marchandises</p></div>
              <div className="rounded-lg bg-[#F0EFEA] p-3"><p className="font-display text-xl text-terracotta">{eur(o.cost_total)}</p><p className="text-[11px] text-stone">Coût d'achat</p></div>
              <div className="rounded-lg bg-[#F0EFEA] p-3"><p className="font-display text-xl text-green-600">{eur(o.margin_total)}</p><p className="text-[11px] text-stone">Marge brute</p></div>
            </div>
            {o.items?.some((i) => !i.cost_price) && <p className="text-[11px] text-amber-700 mt-2 flex items-center gap-1"><Info size={12} /> Certains produits n'ont pas de prix d'achat renseigné.</p>}
          </Section>
        </div>

        {/* RIGHT: status + customer + addresses + payment */}
        <div className="space-y-5">
          <Section icon={Clock} title="Statut de la commande" testid="order-status-section">
            <select value={STATUSES.includes(o.status) ? o.status : ""} disabled={saving} onChange={(e) => setStatus(e.target.value)} data-testid="order-status-select"
              className="w-full rounded-lg border border-border px-3 py-2.5 text-sm bg-white">
              {!STATUSES.includes(o.status) && <option value="">{o.status}</option>}
              {STATUSES.map((st) => <option key={st}>{st}</option>)}
            </select>
            <p className="text-xs text-stone mt-2">Le passage en « Livrée »/« Retirée » envoie l'email de remerciement + avis Google.</p>
          </Section>

          <Section icon={User} title="Client" testid="order-customer">
            <p className="text-sm font-medium text-forest">{o.customer?.name || "—"}</p>
            <Row label="Email" value={o.customer?.email} />
            <Row label="Téléphone" value={o.customer?.phone} />
            {o.customer_id ? <span className="inline-block mt-2 text-[11px] px-2 py-0.5 rounded-full bg-brand/10 text-brand">Compte client</span> : <span className="inline-block mt-2 text-[11px] px-2 py-0.5 rounded-full bg-stone/15 text-stone">Invité</span>}
            {o.discovery_source && <Row label="Découvert via" value={o.discovery_source} />}
          </Section>

          {acc && (
            <Section icon={Building2} title="Compte professionnel" testid="order-account">
              <Row label="Type" value={acc.account_type} />
              <Row label="Société" value={acc.company} />
              <Row label="SIRET" value={acc.siret} />
              <Row label="Conditions" value={acc.payment_terms === "30j_fin_de_mois" ? "30j fin de mois" : "Immédiat"} />
              {acc.payment_terms === "30j_fin_de_mois" && <Row label="Encours autorisé" value={`${(acc.credit_limit || 0).toFixed(0)} € HT`} />}
            </Section>
          )}

          <Section icon={MapPin} title="Livraison & facturation" testid="order-addresses">
            <p className="text-xs font-semibold text-stone uppercase mb-1 flex items-center gap-1">{o.delivery?.mode === "delivery" ? <><Truck size={13} /> Livraison</> : <><Store size={13} /> Retrait en dépôt</>}</p>
            {o.delivery?.mode === "delivery" ? <p className="text-sm text-forest">{o.delivery.address}</p> : <p className="text-sm text-forest">Saint-Victoret (13730)</p>}
            <p className="text-xs font-semibold text-stone uppercase mt-3 mb-1">Adresse de facturation</p>
            <p className="text-sm text-forest">{o.customer?.billing_address || "—"}</p>
          </Section>

          <LoadingPanel logistics={o.logistics} vehicles={vehicles} />

          <Section icon={CreditCard} title="Paiement" testid="order-payment">
            <Row label="Méthode" value={isTerms ? "Virement à 30j fin de mois" : "Carte bancaire (Stripe)"} />
            <Row label="Statut" value={o.payment_status === "paid" ? "Payé" : isTerms ? "À facturer" : "En attente"} />
            {o.session_id && <p className="text-[11px] text-stone mt-1 break-all">Réf. session : {o.session_id}</p>}
          </Section>
        </div>
      </div>
    </div>
  );
}
